/**
 * Cost Centers Catalog Service
 */

import { apiClient } from '@/api/core/apiClient.ts';
import { API_CONFIG } from '@/api/core/apiConfig.ts';
import type { ApiResponse } from '@/api/shared/types.ts';
import { buildQueryString } from '@/api/shared';
import type { CatalogScope } from '@/api/domains/catalog/shared/catalogScope.ts';
import type { LC_GetCostCentersOut, CostCenter } from './costCenters.types.ts';
import { mapCostCenterOutToModel } from './costCenters.mapper.ts';


export class CostCentersCatalogService {
  /**
   * Get available cost centers.
   * By default scoped to the authenticated user's company; pass scope: 'holding'
   * to widen the catalog to every company in the user's holding.
   * Omitting scope preserves the original URL for existing consumers.
   */
  async getCostCenters(
    options?: { scope?: CatalogScope },
    signal?: AbortSignal,
  ): Promise<ApiResponse<CostCenter[]>> {
    const queryString = options?.scope === 'holding'
      ? buildQueryString({ scope: 'holding' })
      : '';
    const endpoint = `${API_CONFIG.ENDPOINTS.CATALOG.COST_CENTERS}${queryString}`;
    // Preserve the original single-argument call when no signal is supplied so existing
    // consumers (and their strict-argument tests) remain unaffected.
    const response = signal
      ? await apiClient.get<LC_GetCostCentersOut>(endpoint, { signal })
      : await apiClient.get<LC_GetCostCentersOut>(endpoint);

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.map(mapCostCenterOutToModel),
      };
    }

    return { ...response, data: null };
  }
}

export const costCentersCatalogService = new CostCentersCatalogService();
